import * as React from 'react';
import {useCallback, useState} from 'react';
import {LayoutChangeEvent, View} from 'react-native';
import {Button} from '../../../shared/components/button';
import {LoadableContent} from '../../../shared/components/loadableContent';
import Screen from '../../../shared/components/screen';
import {CustomScrollView} from '../../../shared/components/scrollView';
import {useStyles} from '../../../shared/hooks/styles';
import {useDimensions} from '../../../shared/hooks/useDimensions';
import {GetPollResponsePayload} from '../../../shared/models/api';
import {Routes, Screens} from '../../../shared/routing';
import {DeviceSize, useDeviceSize} from '../../../shared/theme/helpers';
import {PollFormScreenWrapper} from '../components/pollForm';
import {PollQuestion} from '../components/pollQuestion';
import {PollsTypeSurveyResultScreen} from '../surveyCongrats';
import {Props} from './model';

const getRoutes = (poll: GetPollResponsePayload) => [
  {
    title: 'Опросы и голосования',
    routeName: Screens.Polls,
    webRoute: Routes.polls,
  },
  {
    title: 'Опросы',
    routeName: Screens.PollsTypeSurvey,
    webRoute: Routes.pollsTypeSurvey,
  },
  {
    title: poll?.name || '',
    routeName: Screens.PollsTypeSurveyForm,
    webRoute: Routes.pollsTypeSurveyForm,
  },
];

export default ({poll, onSubmit, onBack, isSurveyCompleteNow}: Props) => {
  const deviceSize = useDeviceSize();
  const {height} = useDimensions();
  const [isBlockScrollAvailable, setScrollState] = useState(false);
  const [blockScrollHeight, setScrollHeightState] = useState(0);

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (deviceSize === DeviceSize.desktop) {
        const top = event.nativeEvent.layout.y;
        const newScrollHeight = height - top - 360;
        const newBlockScrollAvailable = newScrollHeight >= 200 ? height > top + 300 : false;
        if (newBlockScrollAvailable !== isBlockScrollAvailable) {
          setScrollState(newBlockScrollAvailable);
        }
        if (newScrollHeight !== blockScrollHeight) {
          setScrollHeightState(newScrollHeight >= 720 ? 720 : newScrollHeight);
        }
      }
    },
    [isBlockScrollAvailable, blockScrollHeight, height],
  );

  const styles = useStyles(({theme, mediaOnly}) => ({
    sectionContainer: {
      ...mediaOnly(DeviceSize.desktop, {width: 978}),
      marginHorizontal: 'auto',
    },
    questionsBlock: {
      borderRadius: 8,
      backgroundColor: theme.color.primaryNonGreyBackground,
      ...mediaOnly(
        DeviceSize.desktop,
        {paddingVertical: 40, paddingHorizontal: 52},
        {paddingHorizontal: 19, paddingVertical: 30},
      ),
    },
    questionTitle: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          ...theme.fonts.h4,
          marginBottom: 30,
        },
        {
          ...theme.fonts.action,
          marginBottom: 18,
        },
      ),
    },
    question: {
      marginTop: 30,
    },
    questionNotFirst: {
      marginTop: 0,
    },
    button: {
      ...mediaOnly(DeviceSize.mobile, {width: '100%'}, {width: 203}),
    },
  }));

  if (isSurveyCompleteNow && poll?.isPassed && deviceSize !== DeviceSize.desktop) {
    return <PollsTypeSurveyResultScreen />;
  }

  return (
    <Screen>
      <LoadableContent loading={!poll}>
        {poll ? (
          <PollFormScreenWrapper
            submitButton={
              poll.isPassed
                ? () => (
                    <Button
                      id='survey-back'
                      style={styles.button}
                      type='secondary'
                      onPress={onBack}
                      text='Вернуться назад'
                    />
                  )
                : (onPress) => (
                    <Button
                      id='survey-finish'
                      style={styles.button}
                      type='secondary'
                      onPress={onPress}
                      text={'Завершить опрос'}
                    />
                  )
            }
            onSubmit={onSubmit}
            breadcrumbs={getRoutes(poll)}
            containerStyle={styles.sectionContainer}
            poll={poll}>
            <View onLayout={onLayout} style={styles.questionsBlock}>
              <CustomScrollView
                withRefresh={false}
                style={isBlockScrollAvailable ? {maxHeight: blockScrollHeight} : null}>
                {poll?.questions?.map((question, index) => (
                  <PollQuestion
                    disabled={poll?.isPassed}
                    key={question.id}
                    question={question}
                    index={index}
                    style={[styles.question, index === 0 ? styles.questionNotFirst : {}]}
                  />
                ))}
              </CustomScrollView>
            </View>
          </PollFormScreenWrapper>
        ) : null}
      </LoadableContent>
    </Screen>
  );
};
